// Free test calls (server/lib/freeTestCall): what GET /api/voice/test-calls
// returns, and whether a dialled number is one of the offered destinations.
// The server re-checks every call; this only decides what the dialer shows and
// which client-side plan checks it may skip.

export interface FreeTestCallNumber {
  number: string;
  label: string;
  /** 'demo' = the LimePhone AI demo line (own quota, longer limit). */
  kind: 'allowlist' | 'verified' | 'demo';
  remainingToday?: number;
  perDay?: number;
  timeLimitSeconds?: number;
}

export interface DemoLineOption {
  number: string;
  label: string;
  remainingToday: number;
  perDay: number;
  timeLimitSeconds: number;
}

export interface FreeTestCallOptions {
  available: boolean;
  /** The generic bucket's limit; per-number limits are on `numbers[]`. */
  timeLimitSeconds: number;
  remainingToday: number;
  perDay: number;
  numbers: FreeTestCallNumber[];
  /** The AI demo line, when the server has one configured. */
  demo?: DemoLineOption | null;
}

const digits = (s: string) => s.replace(/\D/g, '');

export function isFreeTestDestination(fullNumber: string, options: FreeTestCallOptions | null | undefined): boolean {
  if (!options?.available || !fullNumber) return false;
  const d = digits(fullNumber);
  return d.length >= 8 && options.numbers.some((n) => digits(n.number) === d && (n.remainingToday ?? 1) > 0);
}

/** "2:13" left of a test call capped at `limitSeconds`. */
export function testCallRemaining(elapsedSeconds: number, limitSeconds: number): string {
  const left = Math.max(0, Math.floor(limitSeconds - elapsedSeconds));
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
}

/** Same text as the server's TRIAL_DESTINATION_MESSAGE (server/lib/trialOutboundAllowlist). */
export const TRIAL_OUTBOUND_MESSAGE = 'Trial calls are limited to the AI demo and your verified numbers.';
export const TRIAL_UPGRADE_CTA = 'Upgrade to call anyone';
/** Inline dialer copy when a trial types a number outside the allowlist. */
export const TRIAL_INLINE_LIMIT_MESSAGE =
  'Trial calls go to the AI demo or your verified numbers. Start your plan to call anyone today.';

/** "3 of 5 free test calls left today", or null when the server sent no quota. */
export function testCallsLeftLabel(options: FreeTestCallOptions | null | undefined): string | null {
  if (!options || !Number.isFinite(options.perDay) || options.perDay <= 0) return null;
  const left = Math.max(0, Math.min(options.perDay, Number(options.remainingToday) || 0));
  return `${left} of ${options.perDay} free test call${options.perDay === 1 ? '' : 's'} left today`;
}

/**
 * Trial allowlist (server/lib/trialOutboundAllowlist): may a trialing user dial
 * `fullNumber`? Only the AI demo line and the user's own verified numbers.
 * Remaining daily quota is NOT considered here; the server says so if used up.
 */
export function isTrialAllowedDial(fullNumber: string, options: FreeTestCallOptions | null | undefined): boolean {
  const d = digits(fullNumber || '');
  if (d.length < 8 || !options) return false;
  if (options.demo && digits(options.demo.number) === d) return true;
  return options.numbers.some((n) => (n.kind === 'demo' || n.kind === 'verified') && digits(n.number) === d);
}
